import * as React from 'react';
import { useQuery } from '@apollo/client';
import { exportToCsv } from '@teamsnap/teamsnap-ui/dist/js/utils/export';
import { DateFormat, DateService } from 'frontend-toolkit';
import { Button } from '@teamsnap/teamsnap-ui';

import {
  eventSources,
  Filters,
  getExportFormattedEventDateString,
  getExportFormattedEventTimeString,
  sortEvents,
} from './calendar-utils';

import { formatLocation } from './event';
import CalendarEventsQuery, { EventGQLType } from 'core/gql/queries/calendar-events';

const prefixCSVFilename = 'EventDetails';
const defaultCsvHeader = [
  { label: 'Start Date', key: 'startDate' },
  { label: 'Start Time', key: 'startTime' },
  { label: 'End Time', key: 'endTime' },
  { label: 'Location Name', key: 'locationName' },
  { label: 'Location Detail', key: 'locationDetail' },
  { label: 'Source Name', key: 'calendarSource' },
  { label: 'Type', key: 'type' },
  { label: 'Full Season Name', key: 'fullSeasonName' },
  { label: 'Division Name', key: 'divisionName' },
];

type Props = {
  title: string;
  isDisabled: boolean;
  organizationId: string;
  startDate: Date;
  endDate: Date;
  activeFilters: Filters;
  onClose: () => void;
};

const CalendarExportButton = ({
  title,
  isDisabled,
  organizationId,
  startDate,
  endDate,
  activeFilters,
  onClose,
}: Props) => {
  const [buildingExport, setBuildingExport] = React.useState(false);

  const { loading, error, data } = useQuery<{ calendarEvents: EventGQLType[] }>(CalendarEventsQuery, {
    fetchPolicy: 'cache-and-network',
    skip: isDisabled,
    variables: {
      organizationId,
      startDateTime: startDate,
      endDateTime: endDate,
      seasonIds: activeFilters.seasons,
      divisionIds: activeFilters.divisions,
      locationIds: activeFilters.locations,
      teamIds: activeFilters.teams,
      ...activeFilters.sources?.length > 0 && { calendarSources: activeFilters.sources },
    },
  });

  const exportData = async () => {
    setBuildingExport(true);
    await buildExport();

    setBuildingExport(false);
    onClose();
  };

  const buildEventsExportData = () => {
    if (error) {
      console.log(error);
      return [{}];
    }

    const sortedData = sortEvents(data.calendarEvents);
    const eventExportData = sortedData.map((event) => {
      const startDate = new Date(event.startDateTime);
      const endDate = new Date(event.endDateTime);

      const row = {
        startDate: getExportFormattedEventDateString(startDate),
        startTime: getExportFormattedEventTimeString(startDate),
        endTime: DateService.datesAreSameDay(startDate, endDate)
          ? getExportFormattedEventTimeString(endDate)
          : `${getExportFormattedEventDateString(endDate)} ${getExportFormattedEventTimeString(endDate)}`,
        locationName: event.location && formatLocation(event.location),
        locationDetail: event.location && event.location?.fullAddress,
        calendarSource: eventSources[event.calendarSource],
        type: event.type,
        fullSeasonName: event.seasonFullName,
        divisionName: event.division && event.division?.name,
      };

      addTeamNamesToRow(row, event);

      return row;
    });

    return eventExportData;
  };

  const addTeamNamesToRow = (row, event) => {
    if (event.teams) {
      event.teams.forEach((team, index) => {
        row[`team${index}`] = team.teamName;
      });
    }
  };

  const createHeaders = (exportData) => {
    const exportHeaders = [...defaultCsvHeader];
    let maxColumns = 0;

    exportData.forEach((row) => {
      const numberOfColumns = Object.keys(row).length;
      if (numberOfColumns > maxColumns) {
        maxColumns = numberOfColumns;
      }
    });

    const teamColumnsToAdd = maxColumns - defaultCsvHeader.length;
    if (teamColumnsToAdd > 0) {
      for (let index = 0; index < teamColumnsToAdd; index++) {
        const keyExists = exportHeaders.some((item) => item.key === `team${index}`);
        if (!keyExists) {
          exportHeaders.push({ label: 'Team', key: `team${index}` });
        }
      }
    }

    return exportHeaders;
  };

  const buildExport = () =>
    new Promise((resolve, reject) => {
      setTimeout(() => {
        try {
          let exportData = buildEventsExportData();
          const exportHeaders = createHeaders(exportData);
          exportData = exportData.map((row) => exportHeaders.map((header) => row[header['key']] || ''));

          resolve(
            exportToCsv({
              data: exportData,
              headers: exportHeaders.map((header) => header['label']),
              filename: `${prefixCSVFilename}_${DateService.format(
                startDate,
                DateFormat.DD_MM_YYYY
              )}-${DateService.format(endDate, DateFormat.DD_MM_YYYY)}.csv`,
            })
          );
        } catch (error) {
          console.log(error);
          reject(error);
        }
      }, 200);
    });

  return (
    <>
      <Button
        isDisabled={isDisabled || buildingExport}
        onClick={exportData}
        mods="Button u-spaceLeftSm"
        icon={buildingExport && loading ? 'spin' : 'import'}
      >
        {title}
      </Button>
    </>
  );
};

export default CalendarExportButton;
